import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { GenerateProductReportingResponse } from ':cloud/types/service_proto';
import { ProductTypeCOMS, Project } from ':cloud/types/ts_types';

async function generateProductReporting(
  organizationId: string,
  projectId: string,
  productType: ProductTypeCOMS,
  startDate: string,
  endDate: string,
): Promise<GenerateProductReportingResponse> {
  const path = `/organization-management/v1/organizations/${organizationId}/projects/${projectId}/products:generateProductReporting`;
  const { data } = await APIClientBase.post<GenerateProductReportingResponse>(path, {
    productType,
    startDate,
    endDate,
  });
  return data;
}

export function useGenerateProductReporting(project: Project, productType: ProductTypeCOMS) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fn = useMutation({
    mutationFn: async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      const response = await generateProductReporting(
        project.organizationId,
        project.id,
        productType,
        startDate,
        endDate,
      );
      return response.runId;
    },
    onSuccess: async (runId) => {
      await queryClient.invalidateQueries([
        QueryKey.projects,
        project.organizationId,
        project.id,
        productType,
        runId,
      ]);
    },
    onError: () => {
      toast.show('Unable to generate report. Please shorten the date range or try again later.', {
        variant: 'negative',
      });
    },
  });

  return fn;
}
